/**
 * Generic formatters and text utilities — Phase 3.1.a.1.
 *
 * Pulled out of admin.js. These have zero dependencies on admin SPA state
 * — they're pure functions or DOM-only utilities. Imported by admin.js
 * (Phase 3.1.a) and, eventually, by view modules directly (Phase 3.2).
 */

// ----- HTML/text utilities --------------------------------------------

export function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#39;'}[c]));
}

export function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function looksLikeHtml(value) {
  return typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value);
}

export function stripHtml(value) {
  if (!value) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = value;
  return tmp.textContent || tmp.innerText || '';
}

export function toPlainText(value) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const plain = looksLikeHtml(trimmed) ? stripHtml(trimmed) : trimmed;
  return (plain || '').trim();
}

export function renderHtmlOrText(target, value, { fallback = '(no message preview)', allowHtml = true } = {}) {
  if (!target) return;
  const content = typeof value === 'string' ? value.trim() : '';
  if (!content) {
    target.textContent = fallback;
    target.classList.remove('is-html');
    return;
  }
  if (allowHtml && looksLikeHtml(content)) {
    target.innerHTML = sanitizeHtml(content);
    target.classList.add('is-html');
  } else {
    target.textContent = content;
    target.classList.remove('is-html');
  }
}

export function sanitizeHtml(html) {
  if (!html) return '';
  const template = document.createElement('template');
  template.innerHTML = html;
  const blockedTags = new Set(['script', 'style', 'base', 'iframe', 'object', 'embed', 'link', 'meta']);
  const urlAttrs = new Set(['href', 'src', 'background', 'action', 'formaction', 'srcset']);
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_ELEMENT, null);
  const nodesToRemove = [];

  const isUnsafeUrl = (value) => {
    if (!value) return false;
    const trimmed = String(value).trim();
    if (!trimmed) return false;
    const lower = trimmed.toLowerCase();
    if (lower.startsWith('javascript:') || lower.startsWith('vbscript:')) return true;
    if (lower.startsWith('cid:')) return true;
    if (lower.startsWith('data:')) {
      return !(
        lower.startsWith('data:image/') ||
        lower.startsWith('data:audio/') ||
        lower.startsWith('data:video/')
      );
    }
    return false;
  };

  while (walker.nextNode()) {
    const el = walker.currentNode;
    if (!el || !el.tagName) continue;
    const tag = el.tagName.toLowerCase();
    if (blockedTags.has(tag)) {
      nodesToRemove.push(el);
      continue;
    }
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name === 'style' || name === 'srcdoc' || name.startsWith('on')) {
        el.removeAttribute(attr.name);
        return;
      }
      if (urlAttrs.has(name)) {
        if (name === 'srcset') {
          const safeEntries = String(attr.value || '')
            .split(',')
            .map((entry) => entry.trim())
            .filter((entry) => {
              const url = entry.split(/\s+/)[0];
              return url && !isUnsafeUrl(url);
            });
          if (safeEntries.length) {
            el.setAttribute(attr.name, safeEntries.join(', '));
          } else {
            el.removeAttribute(attr.name);
          }
          return;
        }
        if (isUnsafeUrl(attr.value)) {
          el.removeAttribute(attr.name);
        }
      }
    });
  }
  nodesToRemove.forEach((node) => node.remove());
  return template.innerHTML;
}

// ----- Rich email rendering (sandboxed iframe) ------------------------
// Untrusted email HTML is rendered inside an <iframe sandbox srcdoc> so its CSS
// can run without touching the admin UI, while scripts never execute (the frame
// has no `allow-scripts`) and external images are blocked until the agent opts
// in. buildEmailSrcdoc produces the document string; renderEmailFrame builds the
// frame + its controls. See renderEmailFrame for the sandbox flags.

const _EMAIL_BLOCK_TAGS = new Set(
  ['script', 'iframe', 'object', 'embed', 'base', 'meta', 'link', 'frame', 'frameset', 'applet']);
// Attributes that trigger a network fetch of a (possibly tracking) image/media.
const _EMAIL_MEDIA_ATTRS = ['src', 'srcset', 'background', 'poster'];

function _isRemoteRef(url) {
  const s = String(url == null ? '' : url).trim().toLowerCase();
  if (!s) return false;
  // cid: references an inline attachment we don't serve, so it's "remote" for
  // blocking purposes; data: images are inline and always safe to keep.
  return s.startsWith('http://') || s.startsWith('https://') || s.startsWith('//') || s.startsWith('cid:');
}

/**
 * Build a self-contained document string for an email iframe's `srcdoc`.
 * Keeps <style> + inline `style` (so the email's CSS renders), strips scripts,
 * event handlers and javascript:/vbscript: URLs, and — unless `loadImages` — the
 * src of any remote image so nothing is fetched. Returns { srcdoc, blockedImages }.
 */
export function buildEmailSrcdoc(html, { loadImages = false } = {}) {
  const template = document.createElement('template');
  template.innerHTML = String(html == null ? '' : html);
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_ELEMENT, null);
  const toRemove = [];
  let blockedImages = false;

  while (walker.nextNode()) {
    const el = walker.currentNode;
    if (!el || !el.tagName) continue;
    if (_EMAIL_BLOCK_TAGS.has(el.tagName.toLowerCase())) {
      toRemove.push(el);
      continue;
    }
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value || '';
      if (name === 'srcdoc' || name.startsWith('on')) {
        el.removeAttribute(attr.name);
        return;
      }
      if ((name === 'href' || name === 'xlink:href' || name === 'action' || name === 'formaction')
          && /^\s*(javascript|vbscript):/i.test(value)) {
        el.removeAttribute(attr.name);
        return;
      }
      if (name === 'style') {
        // Keep inline styles, but a remote url() (background image) counts as a
        // blocked image so the "load images" prompt still shows. CSP gates the
        // actual fetch, so we don't need to strip it here.
        if (!loadImages && /url\(\s*['"]?\s*(https?:|\/\/|cid:)/i.test(value)) blockedImages = true;
        return;
      }
      if (_EMAIL_MEDIA_ATTRS.includes(name) && _isRemoteRef(value)) {
        if (!loadImages) {
          el.removeAttribute(attr.name);
          blockedImages = true;
        }
      }
    });
  }
  toRemove.forEach((node) => node.remove());

  const imgSrc = loadImages ? 'https: http: data: cid:' : 'data:';
  const fontSrc = loadImages ? 'https: data:' : 'data:';
  // Belt-and-braces on top of the sandbox: no scripts, no plugins, no framing,
  // no form posts; styles/inline allowed; images gated by the load-images toggle.
  const csp = [
    "default-src 'none'",
    "script-src 'none'",
    "object-src 'none'",
    "frame-src 'none'",
    "child-src 'none'",
    "base-uri 'none'",
    "form-action 'none'",
    "style-src 'unsafe-inline'",
    `font-src ${fontSrc}`,
    `img-src ${imgSrc}`,
  ].join('; ');

  const reset = "html,body{margin:0;padding:10px;background:#fff;color:#111;"
    + "font:13px/1.5 system-ui,-apple-system,'Segoe UI',Roboto,Arial,sans-serif;"
    + "word-break:break-word;overflow-wrap:anywhere;}"
    + "img{max-width:100%;height:auto;}table{max-width:100%;}a{color:#1a56db;}";

  const srcdoc = '<!doctype html><html><head><meta charset="utf-8">'
    + `<meta http-equiv="Content-Security-Policy" content="${csp}">`
    + '<base target="_blank">'
    + `<style>${reset}</style></head><body>${template.innerHTML}</body></html>`;

  return { srcdoc, blockedImages };
}

/**
 * Render rich email HTML into `container` as a sandboxed iframe with an
 * expand/collapse control and a "load external images" prompt. Falls back to
 * plain text (via renderHtmlOrText) when the content isn't HTML.
 */
export function renderEmailFrame(container, html, { fallbackText = '' } = {}) {
  if (!container) return;
  container.innerHTML = '';
  const content = typeof html === 'string' ? html.trim() : '';
  if (!content || !looksLikeHtml(content)) {
    renderHtmlOrText(container, fallbackText || content, { allowHtml: false });
    return;
  }

  let loadImages = false;
  let expanded = false;

  const wrap = document.createElement('div');
  wrap.className = 'email-frame-wrap';

  const bar = document.createElement('div');
  bar.className = 'email-frame-bar';

  const expandBtn = document.createElement('button');
  expandBtn.type = 'button';
  expandBtn.className = 'btn ghost email-frame-expand';

  const imgBtn = document.createElement('button');
  imgBtn.type = 'button';
  imgBtn.className = 'btn ghost email-frame-images';
  imgBtn.textContent = '🖼 Load external images';

  const note = document.createElement('span');
  note.className = 'email-frame-note';
  note.textContent = '🔒 Scripts disabled · links open in a new tab';

  const frame = document.createElement('iframe');
  frame.className = 'email-frame';
  // No allow-scripts → the browser refuses to run any JS in the frame. No
  // allow-same-origin → opaque origin, so the email can't reach the admin page,
  // cookies or storage. allow-popups(-to-escape) lets target=_blank links open
  // as normal tabs.
  frame.setAttribute('sandbox', 'allow-popups allow-popups-to-escape-sandbox');
  frame.setAttribute('referrerpolicy', 'no-referrer');
  frame.setAttribute('loading', 'lazy');
  frame.title = 'Email message';

  function render() {
    const { srcdoc, blockedImages } = buildEmailSrcdoc(content, { loadImages });
    frame.srcdoc = srcdoc;
    imgBtn.style.display = (blockedImages && !loadImages) ? '' : 'none';
    expandBtn.textContent = expanded ? '▲ Collapse' : '▼ Expand';
    frame.classList.toggle('email-frame--expanded', expanded);
  }

  expandBtn.addEventListener('click', () => { expanded = !expanded; render(); });
  imgBtn.addEventListener('click', () => { loadImages = true; render(); });

  bar.appendChild(expandBtn);
  bar.appendChild(imgBtn);
  bar.appendChild(note);
  wrap.appendChild(bar);
  wrap.appendChild(frame);
  container.appendChild(wrap);
  render();
}

export function cssEscapeIdent(value) {
  if (window.CSS && typeof window.CSS.escape === 'function') {
    return CSS.escape(String(value));
  }
  return String(value).replace(/[^a-zA-Z0-9_-]/g, (char) => {
    const hex = char.charCodeAt(0).toString(16).toUpperCase();
    return `\\${hex} `;
  });
}

export function humanizeIdentifier(identifier) {
  if (!identifier) return '';
  const text = String(identifier).trim();
  if (!text) return '';
  const replaced = text.replace(/[_\-.]+/g, ' ');
  return replaced
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => {
      if (!part) return part;
      if (part === part.toUpperCase()) return part;
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
}

// ----- Localization config ---------------------------------------------
//
// The backend owns locale/timezone/currency (LOCALE, TIMEZONE, CURRENCY in
// core/settings.py) and exposes them at /api/app-config. `loadFormattingConfig`
// pulls them once at boot; every formatter below reads this shared state so the
// whole admin UI follows one configuration instead of hardcoding en-GB/GMD in
// dozens of call sites. Defaults match the previous hardcoded values, so nothing
// regresses if the fetch fails or runs before a stray early render.

const _fmtConfig = { locale: 'en-GB', currency: 'GMD', timeZone: undefined };

function _isValidTimeZone(tz) {
  if (!tz) return false;
  try {
    // Throws RangeError for an unknown zone; 'UTC'/'GMT'/IANA names pass.
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function configureFormatting({ locale, currency, timezone } = {}) {
  if (locale) _fmtConfig.locale = String(locale);
  if (currency) _fmtConfig.currency = String(currency);
  // Only adopt a valid IANA/UTC/GMT zone; an unknown value leaves the current
  // setting (default: browser-local) rather than wiping it.
  if (timezone && _isValidTimeZone(timezone)) {
    _fmtConfig.timeZone = String(timezone);
  }
  return _fmtConfig;
}

export function getFormattingConfig() {
  return { ..._fmtConfig };
}

export async function loadFormattingConfig(fetchImpl) {
  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!doFetch) return _fmtConfig;
  try {
    const res = await doFetch('/api/app-config', { credentials: 'same-origin' });
    if (res && res.ok) {
      const data = await res.json();
      configureFormatting({ locale: data.locale, currency: data.currency, timezone: data.timezone });
    }
  } catch {
    // Keep defaults — an unreachable config endpoint must not break formatting.
  }
  return _fmtConfig;
}

// ----- Date/time formatters --------------------------------------------

// Merge the configured time zone into a toLocaleString options object so every
// formatter renders in the configured zone without repeating the plumbing.
function _dtOpts(opts) {
  return _fmtConfig.timeZone ? { ...opts, timeZone: _fmtConfig.timeZone } : { ...opts };
}

export function formatDateTimeShort(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(_fmtConfig.locale, _dtOpts({ day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }));
  } catch {
    return String(iso);
  }
}

export function formatDateTimeFull(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(_fmtConfig.locale, _dtOpts({}));
  } catch {
    return String(iso);
  }
}

// Date only (no time) — for created/paid/expiry dates that shouldn't show a
// spurious 00:00. Replaces ad-hoc `String(x).split('T')[0]` at call sites.
export function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(_fmtConfig.locale, _dtOpts({ day: '2-digit', month: 'short', year: 'numeric' }));
  } catch {
    return String(iso);
  }
}

export function formatRelativeTime(iso) {
  if (!iso) return '—';
  try {
    const value = typeof iso === 'string' ? iso : iso.toString();
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(iso);
    }
    const diffMs = Date.now() - date.getTime();
    const minutes = Math.round(diffMs / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days}d ago`;
    return formatDateTimeShort(date.toISOString());
  } catch {
    return String(iso);
  }
}

export function formatLocalDayKey(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatResolutionDayLabel(dayKey) {
  if (!dayKey) return '';
  const parts = String(dayKey).split('-');
  if (parts.length !== 3) return dayKey;
  const [y, m, d] = parts.map(Number);
  if ([y, m, d].some(Number.isNaN)) return dayKey;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return dayKey;
  return date.toLocaleDateString(_fmtConfig.locale, { weekday: 'short', day: '2-digit', month: 'short' });
}

// ----- Currency & numbers ----------------------------------------------

export function formatCurrency(value, currency) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '—';
  const cur = currency || _fmtConfig.currency;
  try {
    return new Intl.NumberFormat(_fmtConfig.locale, { style: 'currency', currency: cur }).format(Number(value));
  } catch {
    const num = Number(value);
    return `${cur} ${num.toFixed(2)}`;
  }
}

// Plain grouped number (no currency) using the configured locale — for counts,
// balances shown without a currency symbol, etc. Replaces bare `toLocaleString()`.
export function formatNumber(value, opts) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '—';
  try {
    return new Intl.NumberFormat(_fmtConfig.locale, opts || {}).format(Number(value));
  } catch {
    return String(value);
  }
}

// ----- List parsing ----------------------------------------------------

export function parseListInput(value, { lowercase = false } = {}) {
  return String(value == null ? '' : value)
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => (lowercase ? item.toLowerCase() : item));
}

export function normalizeStringList(values, { lowercase = false } = {}) {
  if (values == null) return [];
  let parts;
  if (Array.isArray(values)) {
    parts = values;
  } else if (typeof values === 'string') {
    parts = parseListInput(values);
  } else {
    parts = [String(values)];
  }
  const seen = new Set();
  const out = [];
  parts.forEach((part) => {
    if (part == null) return;
    const trimmed = String(part).trim();
    if (!trimmed) return;
    const value = lowercase ? trimmed.toLowerCase() : trimmed;
    const key = lowercase ? value : value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(value);
  });
  return out;
}

// ----- DOM utilities ---------------------------------------------------

export function debounce(fn, wait = 300) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
}

export function filterTable(tableId, term) {
  term = (term || '').toLowerCase();
  const rows = document.querySelectorAll(`#${tableId} tbody tr`);
  rows.forEach(r => {
    const text = r.textContent.toLowerCase();
    r.style.display = text.includes(term) ? '' : 'none';
  });
}
